import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import {
  ArrowLeft, Check, CheckCheck, ChevronDown, Download, FileText, Image as ImageIcon,
  Loader2, Megaphone, MessageCircleMore, MoreHorizontal, Plus, Search,
  Reply, Send, Smile, SmilePlus, Users, X,
} from "lucide-react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { useSearchParams } from "react-router-dom";
import { store } from "index";
import apiUrl from "slices/apiEndpoint";
import { getDecodeId } from "slices/userProfile/thunk";
import {
  createChatConversation, listChatContacts, listChatConversations, listChatMessages,
  getChatAttachmentUrl, listChatProjects, markChatRead, openChatAttachment, sendChatMessage, uploadChatAttachment,
  setChatReaction, listAdminBroadcastRecipients, sendAdminAppBroadcast, sendAdminChatBroadcast, AdminBroadcastAudience,
} from "features/customChat/customChat.api";
import { ChatAttachment, ChatConversation, ChatMessage, ChatProject, ChatUser } from "features/customChat/types";
import decodeJwt from "helpers/jwt-token-access/jwtdecode";
import "./custom-chat.css";

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "RB";
}

function conversationTitle(conversation: ChatConversation, currentUserId: number) {
  if (conversation.title) return conversation.title;
  const others = conversation.participants.filter((user) => user.id !== currentUserId);
  if (conversation.type === "direct") return others[0]?.name || "Conversation";
  return others.map((user) => user.name).slice(0, 3).join(", ") || "Project conversation";
}

function conversationSubtitle(conversation: ChatConversation, currentUserId: number) {
  if (conversation.type === "direct") {
    return conversation.participants.find((user) => user.id !== currentUserId)?.type?.replace(/_/g, " ") || "Project contact";
  }
  return `${conversation.participants.length} members${conversation.projectId ? ` · Project #${conversation.projectId}` : ""}`;
}

function timeLabel(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
  }
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}

function fullTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }).format(new Date(value));
}

function calendarDay(value: string) {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dateDividerLabel(value: string) {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short", month: "short", day: "numeric",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  }).format(date);
}

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "👀", "🙏"];

function receiptState(message: ChatMessage, conversation: ChatConversation, currentUserId: number) {
  if (message.sender.id !== currentUserId) return null;
  const recipients = conversation.participants.filter((participant) => participant.id !== currentUserId);
  if (!recipients.length) return { state: "sent", label: "Sent" } as const;
  if (recipients.every((participant) => Number(participant.lastReadMessageId || 0) >= message.sequence)) {
    return { state: "read", label: conversation.type === "direct" ? "Seen" : "Seen by everyone" } as const;
  }
  if (recipients.every((participant) => Number(participant.lastDeliveredMessageId || 0) >= message.sequence)) {
    return { state: "delivered", label: "Delivered" } as const;
  }
  return { state: "sent", label: "Sent" } as const;
}

function Avatar({ user, size = "md", online = false }: { user?: ChatUser; size?: "sm" | "md" | "lg"; online?: boolean }) {
  return (
    <span className={`rb-avatar rb-avatar--${size}`}>
      {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <span>{initials(user?.name || "Project contact")}</span>}
      {online && <i className="rb-avatar__presence" />}
    </span>
  );
}

function ImageAttachment({ attachment }: { attachment: ChatAttachment }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let active = true;
    getChatAttachmentUrl(attachment.id)
      .then((result) => { if (active) setUrl(result.url); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [attachment.id]);

  return (
    <button className="rb-image-message" onClick={() => void openChatAttachment(attachment.id)} title={`Open ${attachment.name}`}>
      {url ? <img src={url} alt={attachment.name} /> : <span><ImageIcon size={22} /> Loading image…</span>}
    </button>
  );
}

function EmptyInbox({ onNew, admin = false }: { onNew: () => void; admin?: boolean }) {
  return (
    <div className="rb-chat-empty">
      <span className="rb-chat-empty__icon"><MessageCircleMore size={34} /></span>
      <h2>{admin ? "Reach your RELiBUILD community" : "Keep project communication together"}</h2>
      <p>{admin ? "Send a private chat announcement to selected homeowners, contractors, or real estate agents." : "Message homeowners, teammates, and subcontractors from one secure place."}</p>
      <button className="rb-button rb-button--primary" onClick={onNew}>{admin ? <Megaphone size={17} /> : <Plus size={17} />} {admin ? "New broadcast" : "New conversation"}</button>
    </div>
  );
}

function NewConversationPanel({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (conversation: ChatConversation) => void;
}) {
  const [contacts, setContacts] = useState<ChatUser[]>([]);
  const [projects, setProjects] = useState<ChatProject[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [type, setType] = useState<"direct" | "project" | "team">("direct");
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState<number | undefined>();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    listChatProjects()
      .then(setProjects)
      .catch(() => setError("Projects could not be loaded."));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (type === "project" && !projectId) {
      setContacts([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    listChatContacts({ type, projectId })
      .then((nextContacts) => { if (active) setContacts(nextContacts); })
      .catch(() => { if (active) setError("People could not be loaded."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, projectId, type]);

  const filtered = contacts.filter((contact) => `${contact.name} ${contact.email} ${contact.type}`.toLowerCase().includes(search.toLowerCase()));
  const toggle = (id: number) => setSelected((current) => type === "direct"
    ? (current.includes(id) ? [] : [id])
    : (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const create = async () => {
    if (!selected.length) return setError("Select at least one person.");
    if (type === "project" && !projectId) return setError("Select a project.");
    setSaving(true);
    setError("");
    try {
      const conversation = await createChatConversation({ type, title, projectId, participantIds: selected });
      onCreated(conversation);
      setSelected([]);
      setTitle("");
      setProjectId(undefined);
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Conversation could not be created.");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="rb-drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="rb-drawer" aria-label="New conversation">
        <header className="rb-drawer__header">
          <div><span className="rb-eyebrow">Start something</span><h2>New conversation</h2></div>
          <button className="rb-icon-button" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </header>
        <div className="rb-segmented">
          {(["direct", "project", "team"] as const).map((item) => (
            <button key={item} className={type === item ? "active" : ""} onClick={() => { setType(item); setSelected([]); setProjectId(undefined); setError(""); }}>
              {item === "direct" ? "Direct" : item === "project" ? "Project" : "Team"}
            </button>
          ))}
        </div>
        {type !== "direct" && (
          <label className="rb-field"><span>Conversation name</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={type === "team" ? "e.g. Field operations" : "e.g. Kitchen renovation updates"} /></label>
        )}
        {type === "project" && (
          <label className="rb-field"><span>Linked project</span><select value={projectId || ""} onChange={(event) => { setProjectId(Number(event.target.value) || undefined); setSelected([]); }}><option value="">Select a project</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label>
        )}
        <label className="rb-search rb-search--drawer"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people" /></label>
        <div className="rb-people-list">
          {loading && <div className="rb-inline-state"><Loader2 className="spin" size={19} /> Loading people…</div>}
          {!loading && type === "project" && !projectId && <div className="rb-inline-state">Select a project to see its related people.</div>}
          {!loading && filtered.map((contact) => {
            const checked = selected.includes(contact.id);
            return (
              <button key={contact.id} className={`rb-person-row ${checked ? "selected" : ""}`} onClick={() => toggle(contact.id)}>
                <Avatar user={contact} /><span className="rb-person-row__copy"><strong>{contact.name}</strong><small>{contact.type.replace(/_/g, " ")} · {contact.email}</small></span><span className="rb-check">{checked && <Check size={15} />}</span>
              </button>
            );
          })}
          {!loading && (type !== "project" || projectId) && !filtered.length && <div className="rb-inline-state">No available people found.</div>}
        </div>
        {error && <p className="rb-form-error">{error}</p>}
        <footer className="rb-drawer__footer">
          <button className="rb-button rb-button--ghost" onClick={onClose}>Cancel</button>
          <button className="rb-button rb-button--primary" disabled={saving || !selected.length} onClick={create}>{saving ? <Loader2 className="spin" size={17} /> : <MessageCircleMore size={17} />} Create conversation</button>
        </footer>
      </aside>
    </div>
  );
}

const BROADCAST_AUDIENCES: { value: AdminBroadcastAudience; label: string }[] = [
  { value: "relibuild_app", label: "RELiBUILD app installs" },
  { value: "tradesmens_app", label: "Tradesmen app installs" },
  { value: "homeowner", label: "Homeowners" },
  { value: "contractor", label: "Contractors" },
  { value: "real_estate_agent", label: "Real estate agents" },
];

function AdminBroadcastPanel({ open, onClose, onSent }: {
  open: boolean;
  onClose: () => void;
  onSent: (count: number) => void;
}) {
  const [audience, setAudience] = useState<AdminBroadcastAudience>("homeowner");
  const [recipients, setRecipients] = useState<ChatUser[]>([]);
  const [total, setTotal] = useState(0);
  const [matchedTotal, setMatchedTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [excluded, setExcluded] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const isAppBroadcast = audience === "relibuild_app" || audience === "tradesmens_app";

  useEffect(() => {
    if (!open || isAppBroadcast) {
      if (isAppBroadcast) { setRecipients([]); setTotal(0); setMatchedTotal(0); }
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      listAdminBroadcastRecipients(audience, search)
        .then((result) => {
          if (!active) return;
          setRecipients(result.recipients);
          setTotal(result.total);
          setMatchedTotal(result.matchedTotal);
        })
        .catch(() => active && setError("Recipients could not be loaded."))
        .finally(() => active && setLoading(false));
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [audience, isAppBroadcast, open, search]);

  const changeAudience = (value: AdminBroadcastAudience) => {
    setAudience(value);
    setSearch("");
    setSelected([]);
    setExcluded([]);
    setSelectAll(value === "relibuild_app" || value === "tradesmens_app");
    setError("");
  };

  const isSelected = (id: number) => selectAll ? !excluded.includes(id) : selected.includes(id);
  const toggleRecipient = (id: number) => {
    if (selectAll) {
      setExcluded((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    } else {
      setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    }
  };
  const selectedCount = isAppBroadcast ? 1 : (selectAll ? Math.max(0, total - excluded.length) : selected.length);

  const send = async () => {
    if (!selectedCount) return setError("Select at least one recipient.");
    if (!message.trim()) return setError("Enter a broadcast message.");
    setSending(true);
    setError("");
    try {
      const result = isAppBroadcast
        ? await sendAdminAppBroadcast({ audience: audience === "relibuild_app" ? "relibuild" : "tradesmens", body: message.trim() })
        : await sendAdminChatBroadcast({ audience, body: message.trim(), selectAll, recipientIds: selected, excludedIds: excluded });
      setMessage("");
      setSelected([]);
      setExcluded([]);
      setSelectAll(false);
      onSent(result.sentCount);
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Broadcast could not be sent.");
    } finally { setSending(false); }
  };

  if (!open) return null;
  const audienceLabel = BROADCAST_AUDIENCES.find((item) => item.value === audience)?.label.toLowerCase();
  return (
    <div className="rb-drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !sending && onClose()}>
      <aside className="rb-drawer rb-broadcast-drawer" aria-label="New chat broadcast">
        <header className="rb-drawer__header">
          <div><span className="rb-eyebrow">Admin message</span><h2>New broadcast</h2><p>{isAppBroadcast ? "This appears as a read-only company chat for every installed app." : "Each person receives a private chat from you."}</p></div>
          <button className="rb-icon-button" onClick={onClose} disabled={sending} aria-label="Close"><X size={20} /></button>
        </header>
        <div className="rb-segmented rb-segmented--audience">
          {BROADCAST_AUDIENCES.map((item) => <button key={item.value} className={audience === item.value ? "active" : ""} onClick={() => changeAudience(item.value)}>{item.label}</button>)}
        </div>
        {!isAppBroadcast && <div className="rb-broadcast-toolbar">
          <label className="rb-search rb-search--drawer"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${audienceLabel}`} /></label>
          <button className={`rb-select-all ${selectAll ? "active" : ""}`} onClick={() => { setSelectAll((current) => !current); setSelected([]); setExcluded([]); }}><span className="rb-check">{selectAll && <Check size={15} />}</span>Select all {total}</button>
        </div>}
        {isAppBroadcast ? <div className="rb-broadcast-notice"><span>This sends to all {audienceLabel}. No login or recipient selection is required.</span></div> : <div className="rb-people-list rb-broadcast-people">
          {loading && <div className="rb-inline-state"><Loader2 className="spin" size={19} /> Loading people…</div>}
          {!loading && recipients.map((recipient) => {
            const checked = isSelected(recipient.id);
            return <button key={recipient.id} className={`rb-person-row ${checked ? "selected" : ""}`} onClick={() => toggleRecipient(recipient.id)}><Avatar user={recipient} /><span className="rb-person-row__copy"><strong>{recipient.name}</strong><small>{recipient.email}</small></span><span className="rb-check">{checked && <Check size={15} />}</span></button>;
          })}
          {!loading && !recipients.length && <div className="rb-inline-state">No active users found.</div>}
          {!loading && matchedTotal > recipients.length && <div className="rb-list-note">Showing the first {recipients.length} of {matchedTotal} matches. Search to narrow the list.</div>}
        </div>}
        <label className="rb-field rb-broadcast-message"><span>Message</span><textarea value={message} onChange={(event) => setMessage(event.target.value.slice(0, 4000))} placeholder="Write the announcement users will receive in chat…" rows={4} /><small>{message.length}/4000</small></label>
        {error && <p className="rb-form-error">{error}</p>}
        <footer className="rb-drawer__footer"><span className="rb-recipient-count">{isAppBroadcast ? "All installed apps" : `${selectedCount} selected`}</span><button className="rb-button rb-button--ghost" onClick={onClose} disabled={sending}>Cancel</button><button className="rb-button rb-button--primary" disabled={sending || !selectedCount || !message.trim()} onClick={send}>{sending ? <Loader2 className="spin" size={17} /> : <Megaphone size={17} />} {sending ? "Sending…" : "Send broadcast"}</button></footer>
      </aside>
    </div>
  );
}

const Chat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId = Number(getDecodeId());
  const isAdmin = String(decodeJwt()?.userType || "").toLowerCase() === "admin";
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastNotice, setBroadcastNotice] = useState("");
  const [imageDraft, setImageDraft] = useState<{ file: File; previewUrl: string } | null>(null);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [reactionMenuMessageId, setReactionMenuMessageId] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<"connected" | "connecting" | "offline">("connecting");
  const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set());
  const [typingUsers, setTypingUsers] = useState<Set<number>>(new Set());
  const [error, setError] = useState("");
  const socketRef = useRef<Socket | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const messageFeedRef = useRef<HTMLElement | null>(null);
  const shouldScrollToBottomRef = useRef(true);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedIdRef = useRef<string | null>(null);

  const selected = useMemo(() => conversations.find((item) => item.id === selectedId) || null, [conversations, selectedId]);
  const filteredConversations = useMemo(() => conversations.filter((conversation) => `${conversationTitle(conversation, currentUserId)} ${conversation.lastMessage?.body || ""}`.toLowerCase().includes(search.trim().toLowerCase())), [conversations, currentUserId, search]);

  const reloadConversations = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const next = await listChatConversations();
      setConversations(next);
      setSelectedId((current) => current || next[0]?.id || null);
      setError("");
    } catch (_error) { setError("Conversations could not be loaded. Please refresh and try again."); }
    finally { if (!quiet) setLoading(false); }
  }, []);

  useEffect(() => { void reloadConversations(); }, [reloadConversations]);
  useEffect(() => {
    const requestedConversation = searchParams.get("conversation");
    if (!requestedConversation || !conversations.some((conversation) => conversation.id === requestedConversation)) return;
    setSelectedId(requestedConversation);
    setSearchParams({}, { replace: true });
  }, [conversations, searchParams, setSearchParams]);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add("rb-chat-workspace-active");
    return () => document.body.classList.remove("rb-chat-workspace-active");
  }, []);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const upsertMessage = useCallback((message: ChatMessage) => {
    if (message.conversationId === selectedIdRef.current) {
      const feed = messageFeedRef.current;
      const isNearBottom = !feed || feed.scrollHeight - feed.scrollTop - feed.clientHeight < 140;
      shouldScrollToBottomRef.current = message.sender.id === currentUserId || isNearBottom;
      setMessages((current) => current.some((item) => item.id === message.id) ? current : [...current, message]);
      if (message.sender.id !== currentUserId) void markChatRead(message.conversationId, message.id);
    } else if (message.sender.id !== currentUserId) {
      socketRef.current?.emit("message:delivered", { conversationId: message.conversationId, messageId: message.id });
    }
    setConversations((current) => current.map((conversation) => conversation.id === message.conversationId ? {
      ...conversation, updatedAt: message.createdAt,
      unreadCount: message.conversationId === selectedIdRef.current || message.sender.id === currentUserId ? 0 : conversation.unreadCount + 1,
      lastMessage: { id: message.id, body: message.body, type: message.type, senderUserId: message.sender.id, createdAt: message.createdAt },
    } : conversation).sort((a, b) => new Date(b.lastMessage?.createdAt || b.updatedAt).getTime() - new Date(a.lastMessage?.createdAt || a.updatedAt).getTime()));
  }, [currentUserId]);

  const updateParticipantReceipt = useCallback((
    conversationId: string,
    userId: number,
    messageId: number,
    read: boolean
  ) => {
    setConversations((current) => current.map((conversation) => conversation.id !== conversationId ? conversation : {
      ...conversation,
      participants: conversation.participants.map((participant) => participant.id !== Number(userId) ? participant : {
        ...participant,
        lastDeliveredMessageId: Math.max(Number(participant.lastDeliveredMessageId || 0), Number(messageId || 0)),
        ...(read ? { lastReadMessageId: Math.max(Number(participant.lastReadMessageId || 0), Number(messageId || 0)) } : {}),
      }),
    }));
  }, []);

  useEffect(() => {
    const token = store.getState().Login.token;
    if (!token) return;
    const socket = io(apiUrl, { path: "/socket.io", auth: { token }, transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.on("connect", () => setConnectionState("connected"));
    socket.on("disconnect", () => setConnectionState("offline"));
    socket.on("connect_error", () => setConnectionState("offline"));
    socket.on("message:new", upsertMessage);
    socket.on("message:reaction", ({ messageId, reactions, actorUserId, emoji, active }) => {
      setMessages((current) => current.map((message) => {
        if (message.id !== messageId) return message;
        if (Number(actorUserId) === currentUserId) return { ...message, reactions };
        const next = [...(message.reactions || [])];
        const index = next.findIndex((reaction) => reaction.emoji === emoji);
        if (active) {
          if (index >= 0) next[index] = { ...next[index], count: next[index].count + 1 };
          else next.push({ emoji, count: 1, reactedByCurrentUser: false });
        } else if (index >= 0) {
          const count = next[index].count - 1;
          if (count > 0) next[index] = { ...next[index], count };
          else next.splice(index, 1);
        }
        return { ...message, reactions: next };
      }));
    });
    socket.on("message:delivered", ({ conversationId, userId, messageId }) => {
      updateParticipantReceipt(conversationId, Number(userId), Number(messageId), false);
    });
    socket.on("message:read", ({ conversationId, userId, messageId }) => {
      updateParticipantReceipt(conversationId, Number(userId), Number(messageId), true);
    });
    socket.on("conversation:new", () => void reloadConversations(true));
    socket.on("presence:snapshot", ({ userIds }) => setOnlineUsers(new Set((userIds || []).map(Number))));
    socket.on("presence:update", ({ userId, online }) => setOnlineUsers((current) => { const next = new Set(current); online ? next.add(Number(userId)) : next.delete(Number(userId)); return next; }));
    socket.on("typing:update", ({ conversationId, userId, typing }) => {
      if (conversationId !== selectedIdRef.current || Number(userId) === currentUserId) return;
      setTypingUsers((current) => { const next = new Set(current); typing ? next.add(Number(userId)) : next.delete(Number(userId)); return next; });
    });
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [currentUserId, reloadConversations, updateParticipantReceipt, upsertMessage]);

  useEffect(() => {
    if (!selectedId) { setMessages([]); setReplyTo(null); return; }
    let cancelled = false;
    setLoadingMessages(true);
    shouldScrollToBottomRef.current = true;
    setTypingUsers(new Set());
    setReplyTo(null);
    setReactionMenuMessageId(null);
    listChatMessages(selectedId)
      .then((result) => {
        if (cancelled) return;
        setMessages(result.messages);
        setHasMore(result.hasMore);
        const last = result.messages[result.messages.length - 1];
        if (last) void markChatRead(selectedId, last.id);
        setConversations((current) => current.map((conversation) => conversation.id === selectedId ? { ...conversation, unreadCount: 0 } : conversation));
        socketRef.current?.emit("conversation:join", { conversationId: selectedId });
      })
      .catch(() => setError("Messages could not be loaded."))
      .finally(() => !cancelled && setLoadingMessages(false));
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    socketRef.current?.emit("conversation:active", { conversationId: selectedId });
    return () => { socketRef.current?.emit("conversation:active", { conversationId: null }); };
  }, [selectedId, connectionState]);

  useEffect(() => {
    if (!shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = true;
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const feed = messageFeedRef.current;
      if (feed) feed.scrollTo({ top: feed.scrollHeight, behavior: loadingMessages ? "auto" : "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages.length, typingUsers.size, loadingMessages]);

  const send = async () => {
    const text = messageInput.trim();
    if (!selectedId || (!text && !imageDraft) || sending || uploading) return;
    const pendingReply = replyTo;
    const pendingImage = imageDraft;
    setMessageInput(""); setShowEmoji(false); setReplyTo(null); setImageDraft(null); setSending(true);
    socketRef.current?.emit("typing:update", { conversationId: selectedId, typing: false });
    try {
      if (pendingImage) {
        setUploading(true);
        const attachment = await uploadChatAttachment(selectedId, pendingImage.file);
        upsertMessage(await sendChatMessage(selectedId, text, [attachment.id], "image", pendingReply?.id));
      } else {
        upsertMessage(await sendChatMessage(selectedId, text, [], "text", pendingReply?.id));
      }
      if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    } catch (requestError: any) {
      setMessageInput(text); setReplyTo(pendingReply); setImageDraft(pendingImage || null);
      setError(requestError?.response?.data?.message || "Message was not sent. Please try again.");
    } finally { setSending(false); setUploading(false); }
  };

  const chooseImage = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Only image files can be sent in chat.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setError("Image must be 12 MB or smaller.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (imageDraft?.previewUrl) URL.revokeObjectURL(imageDraft.previewUrl);
    setError("");
    setImageDraft({ file, previewUrl: URL.createObjectURL(file) });
    if (fileRef.current) fileRef.current.value = "";
  };

  const clearImageDraft = () => {
    if (imageDraft?.previewUrl) URL.revokeObjectURL(imageDraft.previewUrl);
    setImageDraft(null);
  };

  const updateTyping = (value: string) => {
    setMessageInput(value);
    if (!selectedId) return;
    socketRef.current?.emit("typing:update", { conversationId: selectedId, typing: value.trim().length > 0 });
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => socketRef.current?.emit("typing:update", { conversationId: selectedId, typing: false }), 1200);
  };

  const loadOlder = async () => {
    if (!selectedId || !messages[0]) return;
    const result = await listChatMessages(selectedId, messages[0].sequence);
    shouldScrollToBottomRef.current = false;
    setMessages((current) => [...result.messages, ...current]);
    setHasMore(result.hasMore);
  };

  const beginReply = (message: ChatMessage) => {
    setReplyTo(message);
    setReactionMenuMessageId(null);
    window.requestAnimationFrame(() => composerRef.current?.focus());
  };

  const toggleReaction = async (message: ChatMessage, emoji: string) => {
    if (!selectedId) return;
    const existing = (message.reactions || []).find((reaction) => reaction.emoji === emoji);
    const active = !existing?.reactedByCurrentUser;
    setReactionMenuMessageId(null);
    try {
      const result = await setChatReaction(selectedId, message.id, emoji, active);
      setMessages((current) => current.map((item) => item.id === message.id
        ? { ...item, reactions: result.reactions }
        : item));
    } catch (_error) {
      setError("Reaction could not be updated.");
    }
  };

  const otherUser = selected?.participants.find((user) => user.id !== currentUserId);
  const selectedOnline = otherUser ? onlineUsers.has(otherUser.id) : false;
  const typingNames = selected?.participants.filter((user) => typingUsers.has(user.id)).map((user) => user.name) || [];
  const openNew = () => isAdmin ? setShowBroadcast(true) : setShowNew(true);

  return (
    <div className="page-content rb-chat-page">
      <div className={`rb-chat-shell ${selected ? "has-selection" : ""}`}>
        <aside className="rb-chat-sidebar">
          <header className="rb-chat-sidebar__header"><div><span className="rb-eyebrow">{isAdmin ? "Admin communication" : "Project communication"}</span><h1>Conversations</h1></div><button className="rb-icon-button rb-icon-button--dark" onClick={openNew} aria-label={isAdmin ? "New broadcast" : "New conversation"}>{isAdmin ? <Megaphone size={19} /> : <Plus size={20} />}</button></header>
          {broadcastNotice && <div className="rb-broadcast-notice"><Check size={15} /><span>{broadcastNotice}</span><button onClick={() => setBroadcastNotice("")} aria-label="Dismiss broadcast confirmation"><X size={14} /></button></div>}
          <label className="rb-search"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search conversations" />{search && <button onClick={() => setSearch("")} aria-label="Clear conversation search"><X size={15} /></button>}</label>
          <div className="rb-chat-filter-row"><button className="active">All</button><span>{conversations.reduce((sum, item) => sum + item.unreadCount, 0)} unread</span></div>
          <div className="rb-conversation-list">
            {loading && Array.from({ length: 5 }).map((_, index) => <div className="rb-conversation-skeleton" key={index}><i /><span><b /><b /></span></div>)}
            {!loading && filteredConversations.map((conversation) => {
              const peer = conversation.participants.find((user) => user.id !== currentUserId);
              return (
                <button key={conversation.id} className={`rb-conversation-row ${conversation.id === selectedId ? "selected" : ""}`} onClick={() => setSelectedId(conversation.id)}>
                  {conversation.type === "direct" ? <Avatar user={peer} online={peer ? onlineUsers.has(peer.id) : false} /> : <span className="rb-group-avatar"><Users size={18} /></span>}
                  <span className="rb-conversation-row__main"><span className="rb-conversation-row__top"><strong>{conversationTitle(conversation, currentUserId)}</strong><time>{timeLabel(conversation.lastMessage?.createdAt || conversation.updatedAt)}</time></span><span className="rb-conversation-row__bottom"><small>{conversation.lastMessage?.body || conversationSubtitle(conversation, currentUserId)}</small>{conversation.unreadCount > 0 && <b>{conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}</b>}</span></span>
                </button>
              );
            })}
            {!loading && !filteredConversations.length && <div className="rb-sidebar-empty"><MessageCircleMore size={24} /><strong>No conversations</strong><span>{search ? "Try a different search." : "Start a secure project conversation."}</span></div>}
          </div>
          <footer className="rb-chat-sidebar__footer"><span className={`rb-connection-dot ${connectionState}`} /> {connectionState === "connected" ? "Chat connected" : connectionState === "connecting" ? "Connecting…" : "Offline · reconnecting"}</footer>
        </aside>

        <main className="rb-thread">
          {!selected ? <EmptyInbox onNew={openNew} admin={isAdmin} /> : <>
            <header className="rb-thread__header"><button className="rb-icon-button rb-thread__back" onClick={() => setSelectedId(null)} aria-label="Back to conversations"><ArrowLeft size={20} /></button>{selected.type === "direct" ? <Avatar user={otherUser} online={selectedOnline} /> : <span className="rb-group-avatar"><Users size={18} /></span>}<div className="rb-thread__identity"><strong>{conversationTitle(selected, currentUserId)}</strong><small>{selectedOnline ? "Online now" : conversationSubtitle(selected, currentUserId)}</small></div><button className="rb-icon-button" aria-label="Conversation options"><MoreHorizontal size={20} /></button></header>
            {error && <div className="rb-chat-alert"><span>{error}</span><button onClick={() => setError("")}><X size={15} /></button></div>}
            <section className="rb-message-feed" ref={messageFeedRef}>
              {hasMore && <button className="rb-load-older" onClick={loadOlder}><ChevronDown size={15} /> Load earlier messages</button>}
              {loadingMessages && <div className="rb-feed-loading"><Loader2 className="spin" size={22} /> Loading conversation…</div>}
              {!loadingMessages && messages.map((message, index) => {
                const mine = message.sender.id === currentUserId;
                const receipt = selected ? receiptState(message, selected, currentUserId) : null;
                const previous = messages[index - 1];
                const grouped = previous?.sender.id === message.sender.id
                  && calendarDay(previous.createdAt) === calendarDay(message.createdAt)
                  && new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime() < 5 * 60 * 1000;
                const showDateDivider = !previous || calendarDay(previous.createdAt) !== calendarDay(message.createdAt);
                return (
                  <React.Fragment key={message.id}>
                  {showDateDivider && <div className="rb-date-divider"><span>{dateDividerLabel(message.createdAt)}</span></div>}
                  <article id={`message-${message.id}`} className={`rb-message ${mine ? "mine" : "theirs"} ${grouped ? "grouped" : ""}`}>
                    {!mine && !grouped && <Avatar user={message.sender} size="sm" />}
                    <div className="rb-message__stack">{!grouped && <div className="rb-message__meta"><strong>{mine ? "You" : message.sender.name}</strong><time>{fullTime(message.createdAt)}</time></div>}<div className="rb-message__bubble">{message.replyTo && <button className="rb-message__quote" onClick={() => document.getElementById(`message-${message.replyTo?.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}><strong>{message.replyTo.senderName}</strong><span>{message.replyTo.deletedAt ? "Message deleted" : message.replyTo.body || "Image"}</span></button>}{message.deletedAt ? <em>Message deleted</em> : <>{message.body && <p>{message.body}</p>}{message.attachments.map((attachment) => attachment.mimeType.startsWith("image/") ? <ImageAttachment key={attachment.id} attachment={attachment} /> : <button className="rb-attachment" key={attachment.id} onClick={() => void openChatAttachment(attachment.id)}><span><FileText size={19} /></span><span><strong>{attachment.name}</strong><small>{Math.max(1, Math.round(attachment.size / 1024))} KB</small></span><Download size={16} /></button>)}</>}{receipt && <span className={`rb-message__receipt ${receipt.state}`} title={receipt.label} aria-label={receipt.label}>{receipt.state === "sent" ? <Check size={14} /> : <CheckCheck size={14} />}</span>}</div>{!!message.reactions?.length && <div className="rb-message__reactions">{message.reactions.map((reaction) => <button key={reaction.emoji} className={reaction.reactedByCurrentUser ? "active" : ""} onClick={() => void toggleReaction(message, reaction.emoji)}>{reaction.emoji}<span>{reaction.count}</span></button>)}</div>}</div>
                    {!message.deletedAt && <div className="rb-message__actions"><button onClick={() => beginReply(message)} title="Reply"><Reply size={14} /></button><button onClick={() => setReactionMenuMessageId((current) => current === message.id ? null : message.id)} title="Add reaction"><SmilePlus size={14} /></button>{reactionMenuMessageId === message.id && <div className="rb-reaction-menu">{QUICK_REACTIONS.map((emoji) => <button key={emoji} onClick={() => void toggleReaction(message, emoji)}>{emoji}</button>)}</div>}</div>}
                  </article>
                  </React.Fragment>
                );
              })}
              {!!typingNames.length && <div className="rb-typing"><span><i /><i /><i /></span>{typingNames.join(", ")} {typingNames.length > 1 ? "are" : "is"} typing</div>}
            </section>
            <footer className="rb-composer-wrap">
              {replyTo && <div className="rb-reply-preview"><Reply size={16} /><span><strong>Replying to {replyTo.sender.id === currentUserId ? "yourself" : replyTo.sender.name}</strong><small>{replyTo.body || (replyTo.attachments.length ? "Attachment" : "Message")}</small></span><button onClick={() => setReplyTo(null)} aria-label="Cancel reply"><X size={16} /></button></div>}
              {imageDraft && <div className="rb-image-draft"><img src={imageDraft.previewUrl} alt="Selected image preview" /><span><strong>{imageDraft.file.name}</strong><small>{Math.max(1, Math.round(imageDraft.file.size / 1024))} KB · Ready to send</small></span><button onClick={clearImageDraft} aria-label="Remove selected image"><X size={17} /></button></div>}
              {showEmoji && <div className="rb-emoji-popover"><EmojiPicker width={320} height={380} onEmojiClick={(emoji: EmojiClickData) => updateTyping(messageInput + emoji.emoji)} /></div>}
              <div className="rb-composer"><input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => chooseImage(event.target.files?.[0])} /><button className="rb-icon-button" disabled={uploading || sending} onClick={() => fileRef.current?.click()} title="Add image" aria-label="Add image">{uploading ? <Loader2 className="spin" size={20} /> : <ImageIcon size={20} />}</button><textarea ref={composerRef} rows={1} value={messageInput} onChange={(event) => updateTyping(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void send(); } }} placeholder={replyTo ? `Reply to ${replyTo.sender.name}` : `Message ${conversationTitle(selected, currentUserId)}`} /><button className="rb-icon-button rb-emoji-button" onClick={() => setShowEmoji((value) => !value)} aria-label="Choose emoji"><Smile size={20} /></button><button className="rb-send-button" disabled={(!messageInput.trim() && !imageDraft) || sending || uploading} onClick={() => void send()} aria-label="Send message">{sending || uploading ? <Loader2 className="spin" size={19} /> : <Send size={19} />}</button></div>
              <small className="rb-composer-hint">Enter to send · Shift + Enter for a new line</small>
            </footer>
          </>}
        </main>
      </div>
      <NewConversationPanel open={showNew} onClose={() => setShowNew(false)} onCreated={(conversation) => { setConversations((current) => current.some((item) => item.id === conversation.id) ? current : [conversation, ...current]); setSelectedId(conversation.id); }} />
      <AdminBroadcastPanel open={showBroadcast} onClose={() => setShowBroadcast(false)} onSent={(count) => { setBroadcastNotice(`Broadcast sent privately to ${count} user${count === 1 ? "" : "s"}.`); void reloadConversations(true); }} />
    </div>
  );
};

export default Chat;
